import React from "react";
import { useLocation } from "react-router-dom";
import "./landing.css";
import { PLATFORM_BRAND, PLATFORM_PHONE, PLATFORM_TAGLINE } from "./config";
import { LegalFooter, MiniLegal } from "./LegalFooter";
import { useLpEngagementBeacon } from "@/lib/lpAnalytics";

/**
 * Shared wrapper for the landing pages.
 *
 * - TrustedForm loads from each lead form itself (see trustedForm.jsx), so
 *   thank-you pages, previews and sample pages never create certificates.
 * - Renders header with dual branding (firm + Conecme marketing attribution)
 *   + trust bar + "how it works" + full TCPA footer.
 * - Sticky mobile "Call Now" bar.
 *
 * Iter-33 additions:
 *   - `preview` → renders a "PREVIEW MODE" ribbon so a Buyer knows they're
 *     looking at an embedded preview (no submission will actually go through).
 *   - `brand`   → replaces the neutral platform brand text in the header +
 *     footer so a Buyer can see what the page looks like with their firm's
 *     name overlaid.
 *
 * Iter-45 additions (dual branding):
 *   - When `brand` (a buyer firm) is set, the header shows the firm as the
 *     primary brand + a "Marketed by Conecme · A Jellis Enterprise LLC
 *     company" attribution line beneath. The footer legal blurb calls out
 *     the marketing-partner relationship explicitly for TCPA compliance.
 *
 * Iter-74.2 additions (buyer-aware phone + policy URLs):
 *   - `brandPhone` → the buyer's own phone. When set, header/footer/mobile
 *     sticky call CTAs use this number instead of `PLATFORM_PHONE`, so
 *     firm-branded custom landing pages don't show two competing numbers.
 *   - `privacyUrl` / `termsUrl` → override the platform's default
 *     `/privacy` and `/terms` links in the footer so a firm-branded page
 *     points at the firm's own policies.
 */
export default function LandingLayout({
  children, hidePhone = false, preview = false, brand = null,
  isSamplePage = false,
  // Iter-74.2 — buyer-aware call/policy overrides. Undefined falls back
  // to platform defaults so every existing generic /lp/<slug> page keeps
  // its historical behaviour.
  brandPhone = null,
  privacyUrl = null,
  termsUrl = null,
  brandLogo = null,
  buyerCode = null,
  // Quiz V2 question screens: no header, footer or call bar — just the quiz and tiny policy links.
  minimal = false,
}) {
  const displayBrand = brand || PLATFORM_BRAND;
  const isFirmBranded = Boolean(brand);
  // When the firm supplies its own phone we prefer it; otherwise the
  // platform placeholder remains so ops can still test-call a page.
  const effectivePhone = (brandPhone && String(brandPhone).trim()) || PLATFORM_PHONE;
  const location = useLocation();

  // Iter-67 — first-party impression + funnel beacon. Only fires on the
  // real /lp/<slug> route; skipped for preview embeds and sample-page
  // demos so analytics don't get polluted by internal previews.
  const slugFromPath = (() => {
    if (preview || isSamplePage) return null;
    const m = (location.pathname || "").match(/^\/lp\/([^/?#]+)/);
    return m ? m[1] : null;
  })();
  useLpEngagementBeacon(slugFromPath);

  return (
    <div className="lp-root" data-testid="landing-root" data-preview={preview ? "1" : "0"}>
      {preview && (
        <div className="lp-preview-ribbon" data-testid="lp-preview-ribbon">
          <strong>PREVIEW MODE</strong>
          <span>&nbsp;— this is what your leads see. No submission will be sent.</span>
        </div>
      )}

      {!minimal && (
      <header className="lp-header" data-testid="lp-header">
        <div className="lp-brand-block">
          <div className="lp-brand" data-testid="lp-header-brand-wrap">
            {brandLogo
              ? <img src={brandLogo} alt={`${displayBrand} logo`} className="lp-brand-logo" data-testid="lp-header-logo" />
              : <span className="lp-brand-dot" />}
            <span data-testid="lp-header-brand">{displayBrand}</span>
            {isFirmBranded && preview && (
              <span className="lp-brand-overlay-tag" data-testid="lp-brand-overlay-tag">
                your firm
              </span>
            )}
          </div>
          {isFirmBranded ? (
            <div className="lp-brand-attribution" data-testid="lp-header-attribution">
              Marketed by <strong>{PLATFORM_BRAND}</strong> · {PLATFORM_TAGLINE}
            </div>
          ) : (
            <div className="lp-brand-attribution lp-brand-attribution-solo"
                 data-testid="lp-header-attribution">
              {PLATFORM_TAGLINE}
            </div>
          )}
        </div>
        {!hidePhone && (
          <a
            href={`tel:${effectivePhone.replace(/-/g, "")}`}
            className="lp-header-cta"
            data-testid="lp-header-call-btn"
            data-lp-cta="phone"
            data-lp-buyer={buyerCode || undefined}
          >
            <span role="img" aria-label="phone">📞</span>
            <span>Call {effectivePhone}</span>
          </a>
        )}
      </header>
      )}

      {children}

      {minimal
        ? <MiniLegal buyerCode={isFirmBranded ? buyerCode : null} privacyUrl={privacyUrl} termsUrl={termsUrl} />
        : <LegalFooter buyerCode={buyerCode} displayBrand={displayBrand} isFirmBranded={isFirmBranded}
                       hidePhone={hidePhone} phone={effectivePhone} privacyUrl={privacyUrl} termsUrl={termsUrl} />}

      {!hidePhone && !minimal && (
        <div className="lp-sticky-call" role="banner" aria-label="Call now">
          <span style={{ fontSize: 13, fontWeight: 700 }}>Free 24/7 Case Review</span>
          <a
            href={`tel:${effectivePhone.replace(/-/g, "")}`}
            data-testid="lp-sticky-call-btn"
            data-lp-cta="phone"
            data-lp-buyer={buyerCode || undefined}
          >
            📞 Call Now
          </a>
        </div>
      )}
    </div>
  );
}
